import { tracker } from '@lessonup/analytics';
import { logger, useDocumentMutation, useDocumentQuery, useLocale } from '@lessonup/client-integration';
import { useErrorContext } from '@lessonup/ui-components';
import { localeToIsoLocaleString } from '@lessonup/utils';
import React, { useCallback } from 'react';
import { MediaHubImageSearchDocument, TrackMediaDownloadDocument } from './MediaHubImageSearch.graphql.generated';
import { MediaHubImageSearchContent } from './components/MediaHubImageSearchContent/MediaHubImageSearchContent';

const DEFAULT_LIMIT = 30;
const DEFAULT_OFFSET = 0;

export interface TrackingContext {
  mediaTarget: 'pin' | 'background';
  actionType: 'add' | 'replace';
}
export interface MediaHubImageSearchFeatureProps {
  onSelection: (url: URL) => void;
  initialQuery: string;
  onSearched?: () => void;
  trackingContext: TrackingContext;
}

export const MediaHubImageSearchFeature: React.FC<MediaHubImageSearchFeatureProps> = ({
  onSelection,
  initialQuery,
  onSearched,
  trackingContext,
}) => {
  const { setError } = useErrorContext();
  const locale = useLocale();
  const queryLanguage = localeToIsoLocaleString(locale);

  const [trackMediaDownload] = useDocumentMutation(TrackMediaDownloadDocument);
  const { data, loading, refetch, error } = useDocumentQuery(MediaHubImageSearchDocument, {
    fetchPolicy: 'cache-first', // This is important, because we only have 5000 requests per hour
    variables: { query: initialQuery, queryLanguage, limit: DEFAULT_LIMIT, offset: DEFAULT_OFFSET },
    skip: !initialQuery,
    onError: (error) => {
      setError({ error, displayMode: 'banner' });
      logger.error(error);
    },
  });

  const onSearch = useCallback(
    (query: string) => {
      refetch({ query, queryLanguage, limit: DEFAULT_LIMIT, offset: DEFAULT_OFFSET });
      onSearched?.();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetch]
  );

  const results = error ? [] : (data?.searchMediaHubImages.nodes ?? []);
  const provider = data?.searchMediaHubImages.provider;

  const onMediaSelection = useCallback(
    (imageUrl: URL, trackDownloadUrl?: URL) => {
      if (trackDownloadUrl && provider?.name.toLowerCase() === 'unsplash') {
        trackMediaDownload({ variables: { trackDownloadUrl: trackDownloadUrl.toString() } });
      }

      if (provider?.name) {
        tracker.events.mediaAction({
          mediaType: 'image',
          mediaTarget: trackingContext.mediaTarget,
          actionType: trackingContext.actionType,
          mediaCreationMethod: 'media_library',
          mediaLibrarySource: provider.name.toLowerCase() === 'unsplash' ? 'unsplash' : 'pexels',
        });
      }

      onSelection(imageUrl);
    },
    [trackMediaDownload, onSelection, provider, trackingContext]
  );

  return (
    <MediaHubImageSearchContent
      onSearch={onSearch}
      results={results}
      provider={provider}
      onMediaSelection={onMediaSelection}
      loading={loading}
      hasError={!!error}
    />
  );
};
